import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase } from 'lucide-react';
import { SEO } from '../components/SEO';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <SEO title="Privacy Policy" description="Training Solutions Privacy Policy - How we collect, use, and protect your personal information." path="/privacy" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/about" className="text-slate-600 hover:text-slate-900 font-medium text-sm">About</Link>
              <Link to="/contact" className="text-slate-600 hover:text-slate-900 font-medium text-sm">Contact</Link>
              <Link to="/terms" className="text-slate-600 hover:text-slate-900 font-medium text-sm">Terms</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="pt-16">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <h1 className="font-outfit text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 mb-10">Last updated: February 2026</p>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">1. Information We Collect</h2>
              <p className="text-slate-600 leading-relaxed">When you register on Training Solutions, we collect personal information including your name, email address, phone number, professional qualifications, work experience, and location. For corporate users, we also collect company name, industry, and business contact details.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">2. How We Use Your Information</h2>
              <ul className="text-slate-600 space-y-2 list-disc pl-6">
                <li>To create and manage your account on our platform</li>
                <li>To match trainers with relevant corporate training requirements using our AI-powered system</li>
                <li>To facilitate communication between trainers and companies</li>
                <li>To send notifications about applications, bookings, and invitations</li>
                <li>To improve our platform's features and user experience</li>
                <li>To send transactional emails (OTP verification, password resets, booking confirmations)</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">3. Information Visibility</h2>
              <p className="text-slate-600 leading-relaxed">Your profile information, including name, email address, phone number, skills, qualifications, and location, is visible to other registered users of the platform. This is essential for the platform's purpose of connecting trainers with companies. You can control what additional information appears on your profile through your dashboard settings.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">4. Data Security</h2>
              <p className="text-slate-600 leading-relaxed">We implement industry-standard security measures to protect your personal information. Passwords are encrypted using bcrypt hashing. All data transmission is secured via HTTPS. We regularly review our security practices to ensure your data remains safe.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">5. Third-Party Services</h2>
              <p className="text-slate-600 leading-relaxed">We use third-party services for specific functions: Resend for email delivery, and AI services for trainer-company matching. These services have their own privacy policies and we ensure they handle your data in compliance with applicable regulations.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">6. Cookies & Analytics</h2>
              <p className="text-slate-600 leading-relaxed">We use essential cookies to maintain your session and authentication state. We may use analytics to understand platform usage patterns and improve our services.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">7. Data Retention</h2>
              <p className="text-slate-600 leading-relaxed">We retain your data for as long as your account is active. If you wish to delete your account and associated data, please contact us at Hello@trainingsolutions.in. We will process deletion requests within 30 days.</p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">8. Your Rights</h2>
              <ul className="text-slate-600 space-y-2 list-disc pl-6">
                <li>Access your personal data stored on our platform</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt out of non-essential communications</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">9. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed">
                For any privacy-related queries, please contact us at{' '}
                <a href="mailto:Hello@trainingsolutions.in" className="text-blue-600 hover:underline">Hello@trainingsolutions.in</a> or visit our{' '}
                <Link to="/contact" className="text-blue-600 hover:underline">Contact page</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-slate-900 mb-3">10. Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed">We may update this privacy policy from time to time. We will notify registered users of significant changes via email or platform notification.</p>
            </section>
          </div>
        </div>

        <footer className="bg-slate-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-sm text-slate-400">© {new Date().getFullYear()} Training Solutions. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </div>
  );
}
